/**
 * How a journal entry reads: an icon, one sentence, and the lines under it.
 *
 * Entries keep names and amounts as they were at the time (see JournalService
 * on the server), so everything here comes from `entry.details` — nothing is
 * looked up again, and a product renamed since still reads as it was sold.
 *
 * `t` and `money` are passed in so this stays free of React.
 */
const ICONS = {
  sale: "🧾",
  stock: "📦",
  product: "🏷️",
  offer: "🎁",
  pack: "🎁",
  credit: "🤝",
  expense: "💸",
  delivery: "🚚",
  supplier: "🚚",
  cash: "💵",
  savings: "🏦",
  preorder: "📅",
  till: "🔐",
  staff: "👥",
  shop: "🏪",
  settings: "⚙️",
  mixing: "🧪",
  catalog: "📚",
  doses: "💧",
};

/** Fields whose values are money, so they are shown with the currency. */
const MONEY_FIELDS = ["price", "cost_price", "amount", "total"];

const formatValue = (field, value, { t, money }) => {
  if (value === null || value === undefined || value === "") return "—";
  if (MONEY_FIELDS.includes(field) && !Number.isNaN(Number(value))) return money(value);
  if (typeof value === "boolean") return t(value ? "journal.values.yes" : "journal.values.no");
  if (Array.isArray(value)) {
    return value
      .map((item) => (item?.name ? `${item.quantity ?? ""}× ${item.name}` : item))
      .join(", ");
  }
  if (typeof value === "object") return t("journal.values.changed");
  return String(value);
};

const fieldLabel = (field, t) => {
  const key = `journal.fields.${field}`;
  const label = t(key);
  return label === key ? field : label;
};

/** "price: 20.00 TND → 22.00 TND", one per changed field. */
const changeLines = (changes, helpers) =>
  Object.entries(changes || {}).map(([field, { from, to }]) =>
    from === undefined
      ? `${fieldLabel(field, helpers.t)}: ${formatValue(field, to, helpers)}`
      : `${fieldLabel(field, helpers.t)}: ${formatValue(field, from, helpers)} → ${formatValue(field, to, helpers)}`,
  );

const itemsLine = (items) =>
  (items || []).map((item) => `${item.quantity}× ${item.name}`).join(", ");

export const describeEntry = (entry, { t, money }) => {
  const helpers = { t, money };
  const d = entry.details || {};
  const [scope] = entry.action.split(".");
  const icon = ICONS[scope] || "•";
  const key = `journal.actions.${entry.action}`;
  const payment = d.payment_method ? t(`common.payment.${d.payment_method}`) : "";

  const params = {
    ...d,
    total: d.total !== undefined ? money(d.total) : "",
    amount: d.amount !== undefined ? money(d.amount) : "",
    payment,
    receipt: d.receipt_number ?? "—",
    product: d.product_name,
    delta: d.delta > 0 ? `+${d.delta}` : d.delta,
  };

  // The drawer's counts are money too
  for (const field of ["counted", "expected", "taken_out", "left_in_drawer"]) {
    if (d[field] !== undefined) params[field] = money(d[field]);
  }
  if (d.source) params.source = t(`journal.sources.${d.source}`);

  // A pre-order's status reads in the owner's language, not as a code
  if (entry.action === "preorder.status") {
    params.from = t(`preorders.status.${d.from}`);
    params.to = t(`preorders.status.${d.to}`);
  }

  let title = t(key, params);
  if (title === key) title = entry.action;

  const lines = [];

  if (entry.action.startsWith("sale.")) {
    if (d.items?.length) lines.push(itemsLine(d.items));
    if (d.credit_tab) lines.push(t("journal.lines.onTab", { name: d.credit_tab }));
    if (d.customer && !d.credit_tab) lines.push(t("journal.lines.customer", { name: d.customer }));
    if (d.discount > 0) lines.push(t("journal.lines.discount", { amount: money(d.discount) }));
    if (d.approved_by) lines.push(t("journal.lines.approvedBelowCost", { name: d.approved_by }));
    else if (d.below_cost) lines.push(t("journal.lines.belowCost"));
    if (d.reason) lines.push(t("journal.lines.reason", { reason: d.reason }));
  }

  if (entry.action.startsWith("cash.") && d.reason) {
    lines.push(t(`pos.drawer.reasons.${d.reason}`));
  }
  if (entry.action === "savings.withdraw" && d.reason) {
    lines.push(t(`savings.withdrawReasons.${d.reason}`));
  }
  if (entry.action === "cash.opened" || entry.action === "cash.closed") {
    const difference = d.difference > 0 ? `+${money(d.difference)}` : money(d.difference);
    lines.push(t("journal.lines.drawerCount", { expected: money(d.expected), difference }));
  }
  if (entry.action === "cash.closed") {
    lines.push(
      t("journal.lines.drawerSplit", { taken: money(d.taken_out), left: money(d.left_in_drawer) }),
    );
  }
  if (entry.action.startsWith("delivery.")) {
    if (d.items?.length) lines.push(itemsLine(d.items));
    if (d.awaiting_prices) lines.push(t("journal.lines.awaitingPrices"));
  }

  if (d.notes) lines.push(d.notes);
  if (d.note) lines.push(d.note);
  if (d.changes) lines.push(...changeLines(d.changes, helpers));
  if (entry.action.startsWith("pack.") && d.items) lines.push(itemsLine(d.items));
  if (entry.action === "doses.setup_changed" && d.products?.length) lines.push(d.products.join(", "));

  const drawerShort = entry.action.startsWith("cash.") && d.difference < 0;

  return {
    icon,
    title,
    lines,
    warn: Boolean(d.below_cost) || entry.action === "till.pin_failed" || drawerShort,
  };
};

/** The type filter's choices — JOURNAL_CATEGORIES on the server. */
export const JOURNAL_CATEGORIES = [
  "sales",
  "stock",
  "catalogue",
  "money",
  "preorders",
  "till",
  "team",
];
